import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { onCompetitionResult } from '../lib/points'
import { useAuth } from '../hooks/useAuth'
import { formatDate } from '../lib/helpers'
import { Modal, Grid, FormField, btnStyle, btnSecStyle } from './CardsPage'

export default function CompetitionsPage() {
  const { club } = useAuth()
  const [fighters, setFighters] = useState([])
  const [competitions, setCompetitions] = useState([])
  const [selectedFighter, setSelectedFighter] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!club) return
    const [f, c] = await Promise.all([
      supabase.from('fighters').select('*').eq('club_id', club.id).order('first_name'),
      supabase.from('competitions').select('*').order('competition_date', { ascending: false }),
    ])
    setFighters(f.data || [])
    setCompetitions(c.data || [])
    if (f.data?.length) setSelectedFighter(f.data[0].id)
    setLoading(false)
  }, [club])

  useEffect(() => { load() }, [load])

  const fighterComps = competitions.filter(c => c.fighter_id === selectedFighter)
  const selectedF = fighters.find(f => f.id === selectedFighter)

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading...</div>

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={selectedFighter} onChange={e => setSelectedFighter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, background: '#fff', minWidth: 180 }}>
          {fighters.map(f => <option key={f.id} value={f.id}>{f.first_name} {f.last_name}</option>)}
        </select>
        <button onClick={() => setShowAdd(true)} style={btnStyle}>+ Add result</button>
      </div>

      {selectedF && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {['gold', 'silver', 'bronze'].map(medal => {
            const count = competitions.filter(c => c.fighter_id === selectedFighter && c.medal === medal).length
            const col = medal === 'gold' ? '#d4a017' : medal === 'silver' ? '#909090' : '#a05020'
            return (
              <div key={medal} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '12px 20px', textAlign: 'center', minWidth: 80 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: col, margin: '0 auto 6px' }} />
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>{count}</div>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'capitalize' }}>{medal}</div>
              </div>
            )
          })}
          <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '12px 20px', textAlign: 'center', minWidth: 80 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>{fighterComps.reduce((s, c) => s + (c.wins || 0), 0)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>Total wins</div>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #eee', overflow: 'hidden' }}>
        {fighterComps.length === 0
          ? <div style={{ padding: '2rem', textAlign: 'center', color: '#888', fontSize: 14 }}>No competition results yet.</div>
          : fighterComps.map((c, i) => {
              const col = c.medal === 'gold' ? '#d4a017' : c.medal === 'silver' ? '#909090' : '#a05020'
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: i < fighterComps.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: col, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{c.tournament_name}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {formatDate(c.competition_date)}{c.weight_class ? ' · ' + c.weight_class : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', textTransform: 'capitalize' }}>{c.medal} · {c.wins} win{c.wins !== 1 ? 's' : ''}</div>
                    {c.submission_used && <div style={{ fontSize: 11, color: '#888' }}>via {c.submission_used}</div>}
                  </div>
                </div>
              )
            })
        }
      </div>

      {showAdd && (
        <AddCompModal
          fighters={fighters}
          defaultFighter={selectedFighter}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load() }}
        />
      )}
    </div>
  )
}

function AddCompModal({ fighters, defaultFighter, onClose, onSaved }) {
  const [form, setForm] = useState({
    fighter_id: defaultFighter || fighters[0]?.id || '',
    tournament_name: '', competition_date: '', medal: 'gold',
    wins: 1, weight_class: '', submission_used: ''
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function save() {
    if (!form.tournament_name || !form.fighter_id) return
    setSaving(true)
    await supabase.from('competitions').insert(form)

    // Award competition points + badges
    if (form.medal && form.fighter_id) {
      const fighter = fighters.find(x => x.id === form.fighter_id)
      if (fighter) await onCompetitionResult(form.fighter_id, fighter.club_id, form.medal, form.tournament_name)
    }

    // Send notification
    const f = fighters.find(x => x.id === form.fighter_id)
    if (f) {
      const medalLabel = { gold: 'Gold', silver: 'Silver', bronze: 'Bronze' }[form.medal] || form.medal
      await supabase.from('notifications').insert({
        fighter_id: f.id,
        club_id: f.club_id,
        type: 'competition',
        title: `${medalLabel} medal at ${form.tournament_name}!`,
        body: `${f.first_name} won ${form.wins} match${form.wins !== 1 ? 'es' : ''}${form.submission_used ? ' via ' + form.submission_used : ''}.`,
      })
    }

    setSaving(false)
    onSaved()
  }

  return (
    <Modal title="Add competition result" onClose={onClose}>
      <Grid>
        <FormField label="Fighter">
          <select value={form.fighter_id} onChange={e => set('fighter_id', e.target.value)}>
            {fighters.map(f => <option key={f.id} value={f.id}>{f.first_name} {f.last_name}</option>)}
          </select>
        </FormField>
        <FormField label="Medal">
          <select value={form.medal} onChange={e => set('medal', e.target.value)}>
            <option value="gold">Gold</option><option value="silver">Silver</option><option value="bronze">Bronze</option>
          </select>
        </FormField>
        <FormField label="Tournament name" fullWidth>
          <input value={form.tournament_name} onChange={e => set('tournament_name', e.target.value)} placeholder="Irish Open 2025" style={{ width: '100%' }} />
        </FormField>
        <FormField label="Date"><input type="date" value={form.competition_date} onChange={e => set('competition_date', e.target.value)} /></FormField>
        <FormField label="Wins"><input type="number" min="0" value={form.wins} onChange={e => set('wins', parseInt(e.target.value) || 0)} /></FormField>
        <FormField label="Weight class"><input value={form.weight_class} onChange={e => set('weight_class', e.target.value)} placeholder="U40kg" /></FormField>
        <FormField label="Submission used">
          <select value={form.submission_used} onChange={e => set('submission_used', e.target.value)}>
            <option value="">None / points</option>
            {['Armbar','Triangle','Rear naked choke','Guillotine','Kimura','Heel hook','Bow & arrow','Other'].map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </Grid>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} style={btnStyle}>{saving ? 'Saving...' : 'Save result'}</button>
        <button onClick={onClose} style={btnSecStyle}>Cancel</button>
      </div>
    </Modal>
  )
}
